import { createSignal, JSXElement } from 'solid-js';
import Modal from '~/components/ui/Modal';
import { ModalSizes, ModalTypes } from '~/components/ui/Modal/Modal.tsx';
import { useAppData } from '~/components/AppData';
import './fsaOnboardingModal.module.scss';
import { useIntl } from '@cookbook/solid-intl';
import { useFSAModalContext } from '~/pages/FSAOnboarding/FSAModalContext.tsx';
import { useToast } from '~/components/ui/Toast';
import { AlertTriangleIcon, ArrowUpRightIcon } from 'lucide-solid';
import { getLocalizedString } from '~/i18n/utils.ts';
import {
  enableFSAWithSuccessToast,
  getTotalActiveConnections,
  getTranslationKey,
  logAndToastFSAError
} from '~/pages/FSAOnboarding/utils.tsx';
import Checkbox from '~/components/ui/Checkbox';
import { externalLinks } from '~/consts.ts';
import { Localized } from '~/i18n';
import { useQueryClient } from '@tanstack/solid-query';
import { getEnvActiveSsoConnectionListQueryKey } from '~/api';
import { Button } from '~/components/ui/Button';
import { Tipper } from '~/components/ui/Tooltip';
import styles from './fsaOnboardingModal.module.scss';

export default function FSAOnboardingWarningModal(): JSXElement {
  const appData = useAppData();
  const intl = useIntl();
  const toaster = useToast();
  const queryClient = useQueryClient();
  const fsaModalContext = useFSAModalContext();

  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [isUserConsentVerified, setIsUserConsentVerified] =
    createSignal<boolean>(false);

  const activeConnections = () =>
    getTotalActiveConnections(
      appData.selectedEnvironment!.type,
      queryClient.getQueryData(
        getEnvActiveSsoConnectionListQueryKey(appData.selectedEnvironment?.id)
      )
    );

  const resetCheckboxState = () => {
    setIsUserConsentVerified(false);
  };

  return (
    <Modal
      open={fsaModalContext.isFSAOnboardingWarningModalOpen}
      size={ModalSizes.LARGE}
      type={ModalTypes.DIALOG}
    >
      {(closeModal) => (
        <>
          <div class={`flex items-center`}>
            <AlertTriangleIcon
              width={24}
              class={`text-[#FFAA00]`}
            />
            <span class={`ml-2 break-words text-[16px] font-medium`}>
              {getLocalizedString(
                getTranslationKey('fsa_setup_warning_title'),
                {
                  intl
                }
              )}
            </span>
          </div>
          <p class={`mt-4`}>
            {getLocalizedString(
              getTranslationKey(
                `fsa_setup_warning_desc.${activeConnections() > 1 ? 'plural' : 'singular'}`
              ),
              {
                intl,
                activeConnections: activeConnections(),
                bold: (str: string) => <span class={`font-bold`}>{str}</span>,
                link: (str: string) => (
                  <a
                    href={externalLinks.kbArticles.fsaMigrationGuide}
                    target={'_blank'}
                    class={`text-sm text-link-primary hover:underline`}
                    data-testid={`fsa-warning-migration-guide`}
                  >
                    {str}
                    <ArrowUpRightIcon
                      strokeWidth={2}
                      width={16}
                      class={`mb-[1px] inline`}
                    />
                  </a>
                )
              }
            )}
          </p>
          <div class={`mt-4 flex flex-col gap-2 ${styles.checkbox}`}>
            <Checkbox
              size={`sm`}
              checked={isUserConsentVerified()}
              onCheckedChange={({ checked }) => {
                setIsUserConsentVerified(!!checked);
              }}
              class={`mt-4`}
              variant={'success'}
            >
              <span class={`text-fg-muted font-medium text-sm`}>
                {getLocalizedString(getTranslationKey('fsa_consent_check'), {
                  intl
                })}
              </span>
            </Checkbox>
          </div>
          <div class={`flex justify-end mt-6`}>
            <Button
              variant={`outline`}
              size={'sm'}
              onClick={() => {
                closeModal();
                resetCheckboxState();
                fsaModalContext.onCancelFsaOnboardingWarningModal?.();
                fsaModalContext.hideFSAOnboardingWarningModal();
              }}
              class={`mr-2`}
              data-testid={`fsa-warning-cancel`}
            >
              <Localized translationKey={'cta_labels.cancel'} />
            </Button>
            <Tipper
              when={!isUserConsentVerified()}
              successTip={getLocalizedString(
                getTranslationKey('confirm_activation_disable_tipper')
              )}
              positioning={{
                placement: 'bottom-end'
              }}
            >
              <Button
                type={'button'}
                size={'sm'}
                disabled={!isUserConsentVerified()}
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    await enableFSAWithSuccessToast({
                      envId: appData.selectedEnvironment!.id,
                      toaster,
                      intl
                    });
                    fsaModalContext.onConfirmFsaOnboardingWarningModal?.();
                  } catch (error) {
                    logAndToastFSAError({
                      error: error as Error,
                      toaster,
                      intl
                    });
                    fsaModalContext.onCancelFsaOnboardingWarningModal?.();
                  } finally {
                    setIsLoading(false);
                    closeModal();
                    resetCheckboxState();
                    fsaModalContext.hideFSAOnboardingWarningModal();
                  }
                }}
                loading={isLoading()}
                color={'primary'}
                data-testid={`fsa-warning-confirm`}
              >
                {getLocalizedString(
                  getTranslationKey('cta_label_confirm_activation'),
                  { intl }
                )}
              </Button>
            </Tipper>
          </div>
        </>
      )}
    </Modal>
  );
}
